// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { featureToggleSelectors, initActionTypes } from '@makemydeal/dr-shared-store';

// actions
import { fetchOpenRepairOrders } from '../actions/openRepairOrdersActionCreators';

// consts/enums
import { VEHICLE_CHANGE_SUCCESS } from '@makemydeal/dr-activities-change-vehicle';
import { OPEN_REPAIR_ORDERS_FETCH_SUCCESS } from '../actionTypes/openRepairOrdersActionTypes';
import { SHOW_REPAIR_ORDERS_SLIDEOUT } from '../actionTypes/baseDealActionTypes';

// interfaces/types
import type { DashMiddleware, DashStore, DashNext } from '@makemydeal/dr-shared-store';
import type { StateTree } from '@makemydeal/dr-dash-types';

// selectors
import * as repairOrderSelectors from '../selectors/repairOrderSelectors';
import * as vehicleSelectors from '../selectors/vehicleSelectors';

// utils
import { logRepairOrdersDrawerOpenedEvent, logRepairOrdersLoadedEvent } from '../utils/repairOrderAnalytics';

const { INIT_SUCCESS } = initActionTypes;

export const middleware: DashMiddleware = (store: DashStore) => (next: DashNext) => (action: AnyFSA) => {
    next(action);

    switch (action.type) {
        // VEHICLE_CHANGE_SUCCESS, not SWITCH_VEHICLE: the latter only opens the loader in open-deal-v2,
        // and VEHICLE_CHANGE_FAILURE does not put the previous vehicle back.
        case INIT_SUCCESS:
        case VEHICLE_CHANGE_SUCCESS: {
            const state = store.getState() as StateTree;
            const vin = vehicleSelectors.getVehicleVin(state);

            if (!featureToggleSelectors.isRoAlertInMVEnabled(state) || !vin) {
                break;
            }

            // One request per car: a multi-scenario deal runs this once per scenario, and scenarios
            // sharing a vin all read the response of whichever of them got here first. The scenarios
            // hydrate in a single tick, so this relies on the request action reaching the reducer
            // synchronously — see the note on the fetch's meta.
            if (repairOrderSelectors.hasRequestedRepairOrders(state, vin)) {
                break;
            }

            store.dispatch(fetchOpenRepairOrders(vin));
            break;
        }
        // Fetch succeeded (with or without open ROs): report what is shown on the indicator so New Relic
        // has the full set of deals to chart the open-RO metrics for US2077492.
        case OPEN_REPAIR_ORDERS_FETCH_SUCCESS: {
            logRepairOrdersLoadedEvent(store.getState() as StateTree);
            break;
        }
        // Only the RO alert dispatches this, and only behind the toggle, but SHOW_REPAIR_ORDERS_SLIDEOUT
        // is a generic base-deal action — guard on the toggle and on the drawer actually opening so a
        // close (payload === false) is not counted as an open.
        case SHOW_REPAIR_ORDERS_SLIDEOUT: {
            const state = store.getState() as StateTree;

            if (featureToggleSelectors.isRoAlertInMVEnabled(state) && action.payload?.showRepairOrdersSlideout) {
                logRepairOrdersDrawerOpenedEvent(state);
            }
            break;
        }
        default:
            break;
    }
};
